// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {applyIssueViewerFieldValue} from '@github-ui/issue-metadata/issue-field-value-optimistic'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {
  type IssueViewerPrimaryReconciliationTarget,
  projectIssueViewerMetadata,
  reconcileFocusedIssueViewerPrimary,
} from '@github-ui/preheat-issues-manager/issue-viewer-primary-reconciliation'
import {queryOptions, useQuery, useQueryClient} from '@github-ui/react-query'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {IssueMetadataRefetchServerQuery} from '@github-ui/server-persisted-queries'
import type {IssueMetadataRefetchServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueMetadataRefetchServerQuery'
import {applyIssueSummaryDelta} from '@github-ui/sub-issues/optimistic-overlay'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useEffect, useMemo, useState} from 'react'

import type {IssueViewerIssue$data} from './__generated__/IssueViewerIssue.graphql'

const PROJECT_ITEMS_CONNECTION_PATH = ['projectItems']
export const LIVE_UPDATE_BATCH_MS = 250

export type IssueMetadata = Pick<
  IssueViewerIssue$data,
  | 'assignedActors'
  | 'labels'
  | 'milestone'
  | 'projectItems'
  | 'agentAssignments'
  | 'linkedPullRequests'
  | 'closedByPullRequestsReferences'
  | 'issueType'
  | 'viewerCanAssign'
  | 'viewerCanLabel'
  | 'viewerCanSetMilestone'
  | 'viewerCanType'
  | 'viewerCanSetFields'
  | 'viewerCanUpdateMetadata'
  | 'issueFieldValues'
>

export type IssueMetadataSource = IssueMetadata & Pick<IssueViewerIssue$data, 'id'>

export type IssueMetadataQueryResult = {
  issue: IssueMetadataSource
  source: 'seed' | 'refetch'
  fetchedBy?: number
  projection?: Extract<NonNullable<IssueMetadataRefetchServerQueryQuery['node']>, {readonly __typename: 'Issue'}>
}

let nextMetadataMountId = 0

export type IssueMetadataAliveNotification = {
  wait?: number
  issue_metadata_updated?: true
  issue_fields_updated?: true
  issue_type_updated?: true
}

export const isIssueMetadataUpdated = (data: IssueMetadataAliveNotification) =>
  data.issue_metadata_updated === true || data.issue_fields_updated === true || data.issue_type_updated === true

export function issueMetadataQueryKey(issueId: string, allowedProjectOwner?: string) {
  return ['issue-viewer', 'issue-metadata', {issueId, allowedProjectOwner: allowedProjectOwner ?? null}] as const
}

export function issueMetadataFromIssue(issue: IssueMetadataSource): IssueMetadataSource {
  return {
    id: issue.id,
    assignedActors: issue.assignedActors,
    labels: issue.labels,
    milestone: issue.milestone,
    projectItems: issue.projectItems,
    agentAssignments: issue.agentAssignments,
    linkedPullRequests: issue.linkedPullRequests,
    closedByPullRequestsReferences: issue.closedByPullRequestsReferences,
    issueType: issue.issueType,
    viewerCanAssign: issue.viewerCanAssign,
    viewerCanLabel: issue.viewerCanLabel,
    viewerCanSetMilestone: issue.viewerCanSetMilestone,
    viewerCanType: issue.viewerCanType,
    viewerCanSetFields: issue.viewerCanSetFields,
    viewerCanUpdateMetadata: issue.viewerCanUpdateMetadata,
    issueFieldValues: issue.issueFieldValues,
  }
}

type IssueMetadataNode = Extract<
  NonNullable<IssueMetadataRefetchServerQueryQuery['node']>,
  {readonly __typename: 'Issue'}
>

function issueMetadataFromResponse(node: IssueMetadataNode): IssueMetadataSource {
  return {
    id: node.id,
    assignedActors: node.assignedActors,
    labels: node.labels,
    milestone: node.milestone,
    projectItems: node.projectItems && {
      edges: node.projectItems.edges?.map(edge => {
        if (!edge?.node) return null
        const {fieldValueByName, project} = edge.node
        return {
          node: {
            id: edge.node.id,
            isArchived: edge.node.isArchived,
            fieldValueByName:
              fieldValueByName?.__typename === 'ProjectV2ItemFieldSingleSelectValue' ? fieldValueByName : null,
            project: {
              id: project.id,
              title: project.title,
              closed: project.closed,
              number: project.number,
              url: project.url,
              template: project.template,
              viewerCanUpdate: project.viewerCanUpdate,
              hasReachedItemsLimit: project.hasReachedItemsLimit,
              field: project.field?.__typename === 'ProjectV2SingleSelectField' ? project.field : null,
            },
          },
        }
      }),
    },
    agentAssignments: node.agentAssignments,
    linkedPullRequests: node.linkedPullRequests,
    closedByPullRequestsReferences: node.closedByPullRequestsReferences,
    issueType: node.issueType,
    viewerCanAssign: node.viewerCanAssign,
    viewerCanLabel: node.viewerCanLabel,
    viewerCanSetMilestone: node.viewerCanSetMilestone,
    viewerCanType: node.viewerCanType,
    viewerCanSetFields: node.viewerCanSetFields,
    viewerCanUpdateMetadata: node.viewerCanUpdateMetadata,
    issueFieldValues: node.issueFieldValues,
  }
}

export function mergeIssueMetadata<T extends IssueMetadataSource>(issue: T, metadata: IssueMetadataSource): T {
  return {
    ...issue,
    assignedActors: metadata.assignedActors,
    labels: metadata.labels,
    milestone: metadata.milestone,
    projectItems: metadata.projectItems,
    agentAssignments: metadata.agentAssignments,
    linkedPullRequests: metadata.linkedPullRequests,
    closedByPullRequestsReferences: metadata.closedByPullRequestsReferences,
    issueType: metadata.issueType,
    viewerCanAssign: metadata.viewerCanAssign,
    viewerCanLabel: metadata.viewerCanLabel,
    viewerCanSetMilestone: metadata.viewerCanSetMilestone,
    viewerCanType: metadata.viewerCanType,
    viewerCanSetFields: metadata.viewerCanSetFields,
    viewerCanUpdateMetadata: metadata.viewerCanUpdateMetadata,
    issueFieldValues: metadata.issueFieldValues,
  }
}

export async function fetchIssueMetadata(
  issueId: string,
  fetchedBy: number,
  allowedProjectOwner?: string,
  signal?: AbortSignal,
): Promise<IssueMetadataQueryResult> {
  const searchParams = new URLSearchParams({id: issueId, queryId: IssueMetadataRefetchServerQuery})
  if (allowedProjectOwner) searchParams.set('allowedOwner', allowedProjectOwner)

  const response = await verifiedFetchJSON(`/issues/issue-metadata-refetch.json?${searchParams}`, {
    signal,
    credentials: 'include',
  })
  if (!response.ok) throw new Error(`Issue metadata refetch failed (${response.status})`)

  const json = (await response.json()) as {data?: IssueMetadataRefetchServerQueryQuery; errors?: unknown[]}
  const node = json.data?.node
  if (node?.__typename !== 'Issue') throw new Error('Issue metadata refetch returned no issue')

  return {issue: issueMetadataFromResponse(node), source: 'refetch', fetchedBy, projection: node}
}

export function issueMetadataQueryOptions(issue: IssueMetadataSource, fetchedBy: number, allowedProjectOwner?: string) {
  return queryOptions({
    // A mount ID is result provenance, not resource identity. Keeping it out lets Alive target a stable key.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: issueMetadataQueryKey(issue.id, allowedProjectOwner),
    queryFn: ({signal}) => fetchIssueMetadata(issue.id, fetchedBy, allowedProjectOwner, signal),
    initialData: (): IssueMetadataQueryResult => ({
      issue: issueMetadataFromIssue(issue),
      source: 'seed',
      fetchedBy,
    }),
    staleTime: Infinity,
    gcTime: DEFAULT_RELAY_CACHE_TTL,
  })
}

export function useIssueMetadata<T extends IssueMetadataSource>(
  issue: T,
  allowedProjectOwner?: string,
  updatesChannel?: string | null,
  primaryReconciliation?: IssueViewerPrimaryReconciliationTarget,
) {
  const queryClient = useQueryClient()
  const [mountId] = useState(() => ++nextMetadataMountId)
  const queryOptionsForIssue = useMemo(
    () => issueMetadataQueryOptions(issue, mountId, allowedProjectOwner),
    [allowedProjectOwner, issue, mountId],
  )
  const {data} = useQuery(queryOptionsForIssue)
  const liveQueryKey = useMemo(
    () => issueMetadataQueryKey(issue.id, allowedProjectOwner),
    [allowedProjectOwner, issue.id],
  )
  useInvalidateOnAliveUpdate<IssueMetadataAliveNotification>(updatesChannel, liveQueryKey, {
    filter: isIssueMetadataUpdated,
    throttleTimeout: LIVE_UPDATE_BATCH_MS,
  })
  const projection = data.source === 'refetch' ? data.projection : undefined
  useEffect(() => {
    if (!projection) return
    const reconcile = async () => {
      try {
        await reconcileFocusedIssueViewerPrimary({
          target: primaryReconciliation,
          issueId: issue.id,
          queryClient,
          project: primary =>
            projectIssueViewerMetadata(primary, projection, {includeProjectItems: !allowedProjectOwner}),
        })
      } catch (error) {
        reportError(error instanceof Error ? error : new Error('Issue metadata reconciliation failed', {cause: error}))
      }
    }
    void reconcile()
  }, [allowedProjectOwner, issue.id, primaryReconciliation, projection, queryClient])
  const targets = useMemo(() => [{queryKey: queryOptionsForIssue.queryKey}], [queryOptionsForIssue.queryKey])
  const connections = useMemo(() => [{ownerId: issue.id, connectionPath: PROJECT_ITEMS_CONNECTION_PATH}], [issue.id])
  const trustedBase = useMemo<IssueMetadataQueryResult>(
    () =>
      data.fetchedBy === mountId ? data : {issue: issueMetadataFromIssue(issue), source: 'seed', fetchedBy: mountId},
    [data, issue, mountId],
  )
  const optimisticResult = useOptimisticData(trustedBase, {
    connections,
    targets,
    applyEntityFieldItem: applyIssueViewerFieldValue,
    applyEntityFieldValuesDelta: applyIssueSummaryDelta,
  })

  return {
    issue: mergeIssueMetadata(issue, optimisticResult.issue),
    queryKey: queryOptionsForIssue.queryKey,
  }
}
